// src/pages/StudentCoco.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const REQUIRED = 3;

// KA student emails carry the 2-digit grad year (jane.smith27@...). → 4-digit year or null.
function gradYearFromEmail(email) {
  const m = String(email || '').toLowerCase().match(/(\d{2})@/);
  return m ? 2000 + parseInt(m[1], 10) : null;
}

function fmtDeadline(v) {
  const d = v?.toDate ? v.toDate() : (typeof v === 'string' ? new Date(v) : null);
  if (!d || isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Amman',
  }).format(d);
}

const C = {
  navy: 'var(--ka-navy, #233C5F)',
  gold: 'var(--ka-gold, #A29164)',
  red: 'var(--ka-red, #E83530)',
  white: 'var(--ka-white, #ffffff)',
  g100: 'var(--ka-gray-100, #f3f4f6)',
  g200: 'var(--ka-gray-200, #e5e7eb)',
  g500: 'var(--ka-gray-500, #6b7280)',
  g700: 'var(--ka-gray-700, #374151)',
};

const styles = {
  page: { minHeight: '100vh', background: C.g100, padding: '24px 16px' },
  wrap: { maxWidth: 720, margin: '0 auto' },
  header: { background: C.navy, color: C.white, borderRadius: '14px 14px 0 0', padding: '24px 24px 20px' },
  h1: { margin: 0, fontSize: 24, fontWeight: 800 },
  seasonName: { marginTop: 4, fontSize: 14, color: 'rgba(255,255,255,0.75)' },
  deadline: { display: 'inline-block', marginTop: 14, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 12px', fontSize: 13 },
  card: { background: C.white, borderRadius: '0 0 14px 14px', padding: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' },
  section: { marginBottom: 26 },
  label: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.g500, marginBottom: 10 },
  sportRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  sportBtn: (active) => ({
    flex: '1 1 120px', padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
    border: `1.5px solid ${active ? C.navy : C.g200}`, background: active ? C.navy : C.white,
    color: active ? C.white : C.g700, fontWeight: 700, fontSize: 14,
  }),
  note: { marginTop: 12, background: 'var(--ka-gold-pale, #faf6ec)', border: `1px solid ${C.gold}`, borderRadius: 8, padding: '12px 14px', fontSize: 13, color: C.navy, lineHeight: 1.5 },
  input: { width: '100%', padding: '11px 14px', border: `1.5px solid ${C.g200}`, borderRadius: 8, fontSize: 16, boxSizing: 'border-box', marginTop: 10 },
  slot: (filled) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 8,
    border: `1.5px ${filled ? 'solid' : 'dashed'} ${filled ? C.navy : C.g200}`, background: filled ? C.white : C.g100,
  }),
  rankBadge: { width: 28, height: 28, borderRadius: '50%', background: C.gold, color: C.navy, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 },
  pickRow: (chosen, full) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px',
    borderRadius: 10, marginBottom: 8, border: `1.5px solid ${chosen ? C.navy : C.g200}`,
    background: chosen ? 'rgba(35,60,95,0.04)' : C.white, cursor: (!chosen && full) ? 'not-allowed' : 'pointer',
    opacity: (!chosen && full) ? 0.5 : 1,
  }),
  pill: (chosen) => ({ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, border: `1.5px solid ${chosen ? C.navy : C.g200}`, color: chosen ? C.navy : C.g500, whiteSpace: 'nowrap' }),
  submit: (ok) => ({ width: '100%', padding: 14, marginTop: 8, borderRadius: 10, border: 'none', fontSize: 15, fontWeight: 800, cursor: ok ? 'pointer' : 'not-allowed', background: ok ? C.gold : C.g200, color: ok ? C.navy : C.g500 }),
  banner: (kind) => ({
    borderRadius: 10, padding: '14px 16px', fontSize: 14, lineHeight: 1.5, marginBottom: 18,
    background: kind === 'ok' ? '#ecfdf5' : kind === 'err' ? '#fef2f2' : '#fff7ed',
    border: `1.5px solid ${kind === 'ok' ? '#6ee7b7' : kind === 'err' ? '#fecaca' : '#fdba74'}`,
    color: kind === 'ok' ? '#065f46' : kind === 'err' ? '#b91c1c' : '#9a3412',
  }),
};

export default function StudentCoco() {
  const { user, isStudent, studentSisId } = useAuth();
  const email = user?.email || '';
  const gradYear = useMemo(() => gradYearFromEmail(email), [email]);

  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(null);
  const [activities, setActivities] = useState([]);
  const [choices, setChoices] = useState([]);     // ordered activityIds
  const [onSport, setOnSport] = useState(null);    // null | true | false
  const [sportName, setSportName] = useState('');
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const sQ = await getDocs(query(collection(db, 'cocoSeasons'),
          where('status', '==', 'signups_open'), limit(1)));
        if (sQ.empty) { if (!cancelled) { setSeason(null); setLoading(false); } return; }
        const sDoc = sQ.docs[0];
        const seasonData = { id: sDoc.id, ...sDoc.data() };

        const aQ = await getDocs(query(collection(db, 'cocoActivities'),
          where('seasonId', '==', sDoc.id)));
        const acts = aQ.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((a) => a.active !== false)
          .filter((a) => a.assignmentOnly !== true)
          .filter((a) => {
            const r = a.gradYearRestriction;
            if (!Array.isArray(r) || r.length === 0) return true;
            return gradYear != null && r.map(Number).includes(gradYear);
          })
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));

        let ex = null;
        if (studentSisId) {
          const pSnap = await getDoc(doc(db, 'cocoPreferences', `${sDoc.id}_${studentSisId}`));
          if (pSnap.exists()) ex = pSnap.data();
        }

        if (cancelled) return;
        setSeason(seasonData);
        setActivities(acts);
        if (ex) {
          setExisting(ex);
          setChoices((ex.choices || []).filter((id) => acts.some((a) => a.id === id)));
          setOnSport(ex.onCompetitiveSport === true);
          setSportName(ex.sportName || '');
        }
      } catch (e) {
        console.error('StudentCoco load:', e);
        if (!cancelled) setError('Could not load co-curriculars. Please refresh, or contact IT if it continues.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentSisId, gradYear]);

  const toggle = (id) => {
    setError(''); setDone(false);
    setChoices((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= REQUIRED) return prev;
      return [...prev, id];
    });
  };

  const canSubmit = isStudent && choices.length === REQUIRED && onSport !== null && !submitting;

  const submit = async () => {
    setError(''); setSubmitting(true);
    try {
      const fns = getFunctions(getApp(), 'us-central1');
      await httpsCallable(fns, 'submitCocoPreferences')({
        choices, onCompetitiveSport: onSport === true, sportName,
      });
      setDone(true);
    } catch (e) {
      setError(e?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={styles.page}><div style={styles.wrap}><div style={styles.card}>Loading…</div></div></div>;
  }

  if (!season) {
    return (
      <div style={styles.page}><div style={styles.wrap}>
        <div style={styles.header}><h1 style={styles.h1}>Co-Curricular Signups</h1></div>
        <div style={styles.card}>Co-curricular signups aren’t open right now. Check back when your advisor lets you know the window is open.</div>
      </div></div>
    );
  }

  const deadline = fmtDeadline(season.signupClosesAt);

  return (
    <div style={styles.page}><div style={styles.wrap}>
      <div style={styles.header}>
        <h1 style={styles.h1}>Co-Curricular Signups</h1>
        <div style={styles.seasonName}>{season.name || season.id}</div>
        {deadline && <div style={styles.deadline}>⏰ Submit by {deadline}</div>}
      </div>

      <div style={styles.card}>
        {!isStudent && <div style={styles.banner('warn')}>Preview mode — only student accounts can submit a ballot.</div>}
        {done && <div style={styles.banner('ok')}>You’re all set! Your choices are saved. You can come back and change them any time before the deadline.</div>}
        {existing && !done && <div style={styles.banner('warn')}>You’ve already submitted. Edit below and re-submit to update your choices before the deadline.</div>}
        {error && <div style={styles.banner('err')}>{error}</div>}

        {/* Competitive sport */}
        <div style={styles.section}>
          <div style={styles.label}>Are you on a competitive sports team this season?</div>
          <div style={styles.sportRow}>
            <button style={styles.sportBtn(onSport === true)} onClick={() => { setOnSport(true); setDone(false); }}>Yes</button>
            <button style={styles.sportBtn(onSport === false)} onClick={() => { setOnSport(false); setSportName(''); setDone(false); }}>No</button>
          </div>
          {onSport === true && (
            <>
              <input style={styles.input} placeholder="Which sport? (optional)" value={sportName}
                     onChange={(e) => setSportName(e.target.value)} maxLength={120} />
              <div style={styles.note}>
                Your sport counts as your co-curricular, so you won’t be placed into one of the activities below by default.
                You still rank three choices in case you switch sports or get injured later — we’ll keep them on file.
              </div>
            </>
          )}
        </div>

        {/* Ranking */}
        <div style={styles.section}>
          <div style={styles.label}>Your top 3 (tap to add in order)</div>
          {[0, 1, 2].map((i) => {
            const id = choices[i];
            const a = id && activities.find((x) => x.id === id);
            return (
              <div key={i} style={styles.slot(!!a)}>
                <div style={styles.rankBadge}>{i + 1}</div>
                <div style={{ flex: 1, fontWeight: a ? 700 : 400, color: a ? C.navy : C.g500 }}>
                  {a ? a.name : 'Not chosen yet'}
                </div>
                {a && <button onClick={() => toggle(id)} style={{ ...styles.pill(true), cursor: 'pointer', border: 'none', background: C.g100 }}>Remove</button>}
              </div>
            );
          })}
        </div>

        {/* Available */}
        <div style={styles.section}>
          <div style={styles.label}>Available co-curriculars</div>
          {activities.length === 0 && <div style={{ color: C.g500 }}>No activities are posted yet.</div>}
          {activities.map((a) => {
            const rank = choices.indexOf(a.id);
            const chosen = rank >= 0;
            const full = choices.length >= REQUIRED;
            return (
              <div key={a.id} style={styles.pickRow(chosen, full)} onClick={() => (chosen || !full) && toggle(a.id)}>
                <div>
                  <div style={{ fontWeight: 700, color: C.navy }}>{a.name}</div>
                  {a.description && <div style={{ fontSize: 13, color: C.g500, marginTop: 2 }}>{a.description}</div>}
                </div>
                <div style={styles.pill(chosen)}>{chosen ? `Choice ${rank + 1}` : (full ? 'Full' : 'Add')}</div>
              </div>
            );
          })}
        </div>

        <button style={styles.submit(canSubmit)} disabled={!canSubmit} onClick={submit}>
          {submitting ? 'Submitting…'
            : onSport === null ? 'Answer the sports question to continue'
            : choices.length < REQUIRED ? `Pick ${REQUIRED - choices.length} more`
            : existing ? 'Update my choices' : 'Submit my choices'}
        </button>
      </div>
    </div></div>
  );
}
